import { TILE_SIZE, FOOT_HALF_W, FOOT_HALF_H } from './events';

/**
 * Turning buildings into walls, on the client.
 *
 * THIS IS A MIRROR OF `server/game/zones.js`, AND THAT IS A REAL COST. It is
 * the second such mirror in this codebase — `pathfind.js` already mirrors
 * `collision.js` — and it is here for the same reason: the editor has to show
 * what the game will do without a round trip per brush stroke.
 *
 * THE SERVER'S COPY IS THE AUTHORITY. Its `compileZones` is what produces the
 * walls players actually collide with. This one only decides what the editor
 * draws and what the reachability shading walks over. If they disagree, the
 * editor lies — a building would appear open and be solid, or vice versa — so
 * the server file carries a note pointing back here.
 *
 * WHAT IS DELIBERATELY NOT MIRRORED: `zoneProblems`. Warnings come back from
 * the server on save, so the wording, the thresholds and the list of things
 * worth warning about live in exactly one place. Only the geometry is copied,
 * because only the geometry has to be instant.
 */

/** Default wall tile, matching the placeholder tileset. */
export const WALL_GID = 3;

/**
 * Minimum door width per side, and it is asymmetric for a real reason.
 *
 * Feet are 18 wide and 10 tall against 16px tiles, so a horizontal gap (a north
 * or south door) constrains WIDTH and needs two tiles, while a vertical one
 * constrains HEIGHT and fits in one. Commit 402d6d1 is where that rule comes
 * from.
 */
export const MIN_DOOR = {
  n: Math.ceil((FOOT_HALF_W * 2) / TILE_SIZE),
  s: Math.ceil((FOOT_HALF_W * 2) / TILE_SIZE),
  e: Math.ceil((FOOT_HALF_H * 2) / TILE_SIZE),
  w: Math.ceil((FOOT_HALF_H * 2) / TILE_SIZE),
};

/** Which tiles a door opens. Corners are excluded — opening one leaves a
 *  diagonal slit that is passable from neither side. */
function doorTiles(zone, door) {
  const {
    x, y, w, h,
  } = zone;
  const span = Math.max(1, door.span || 1);
  const at = Math.max(0, door.at || 0);
  const tiles = [];

  if (door.side === 'n' || door.side === 's') {
    const ty = door.side === 'n' ? y : y + h - 1;
    for (let i = at; i < Math.min(at + span, w - 1); i += 1) {
      if (i >= 1) tiles.push([x + i, ty]);
    }
  } else {
    const tx = door.side === 'e' ? x + w - 1 : x;
    for (let i = at; i < Math.min(at + span, h - 1); i += 1) {
      if (i >= 1) tiles.push([tx, y + i]);
    }
  }

  return tiles;
}

/**
 * Stamp every zone's perimeter into a copy of the painted walls.
 *
 * Pure, and returns a new array — the editor holds the painted layer while
 * previewing zones over it, and mutating it would make a zone permanent the
 * moment it was drawn.
 */
export function compileZones(walls, zones, widthTiles, heightTiles, wallGid = WALL_GID) {
  const out = [...walls];
  if (!Array.isArray(zones) || zones.length === 0) return out;

  const at = (tx, ty) => ty * widthTiles + tx;
  const inBounds = (tx, ty) => tx >= 0 && ty >= 0 && tx < widthTiles && ty < heightTiles;

  zones.forEach((zone) => {
    const {
      x, y, w, h,
    } = zone;
    if (!(w >= 2 && h >= 2)) return;

    /**
     * What this building is made of. Per zone, falling back to the map-wide
     * argument and then to WALL_GID.
     *
     * `||` rather than `??`: zero on a tile layer means "empty", so a zone
     * carrying 0 would compile to a building you can walk straight through.
     * Treating 0 as "unset" is the only reading that cannot produce that.
     */
    const gid = zone.wallGid || wallGid;

    for (let tx = x; tx < x + w; tx += 1) {
      for (let ty = y; ty < y + h; ty += 1) {
        const edge = tx === x || ty === y || tx === x + w - 1 || ty === y + h - 1;
        if (edge && inBounds(tx, ty)) out[at(tx, ty)] = gid;
      }
    }

    // Doors after the perimeter, never before — the loop above would fill them
    // straight back in.
    (zone.doors || []).forEach((door) => {
      doorTiles(zone, door).forEach(([tx, ty]) => {
        if (inBounds(tx, ty)) out[at(tx, ty)] = 0;
      });
    });
  });

  return out;
}

/** How many tiles this door actually opens — what the editor shows next to the
 *  minimum, so "2 of 2" reads as fine and "1 of 2" reads as a problem. */
export const doorWidth = (zone, door) => doorTiles(zone, door).length;

/**
 * Which building a point is inside, or null.
 *
 * MIRRORS `zoneAt` in server/game/zones.js, which stays the authority: it
 * decides whether a claim is allowed, and this only decides whether to offer
 * one. They must agree, because a prompt that appears where the server would
 * refuse is worse than no prompt — it is an instruction that does nothing.
 *
 * WORLD UNITS AND FOOT SPACE, unlike everything above it in this file. The
 * zones the editor works with are in TILES, straight off the document; the ones
 * that arrive in `WELCOME` have been through `parseMap` and are in world units.
 * Same field names, different scale, and mixing them up puts the answer
 * sixteen times away from where the player is standing.
 *
 * @param {{zones?: object[]}} map a map as the client receives it
 * @param {number} footX world units
 * @param {number} footY world units, feet rather than entity centre
 */
export function zoneAt(map, footX, footY) {
  const zones = map?.zones || [];

  for (let i = 0; i < zones.length; i += 1) {
    const z = zones[i];
    if (footX >= z.x && footX < z.x + z.width
      && footY >= z.y && footY < z.y + z.height) {
      return z;
    }
  }
  return null;
}

export default compileZones;
